import React, { useState, useEffect } from 'react';
import { 
  getAllFeeSettings,
  updatePlatformFee,
  updateProcessorFee,
  updateTieredFee,
  updateFeeSplit,
  toggleFeeActive
} from '../../services/api';
import { 
  FiRefreshCw, 
  FiAlertCircle,
  FiCheck,
  FiShield,
  FiCreditCard,
  FiFilter
} from 'react-icons/fi';
import { 
  TbArrowsSplit,
  TbReceiptTax
} from 'react-icons/tb';
import FeeCard from './components/FeeCard';
import FeeEditForm from './components/FeeEditForm';

function FeeSettings() {
  const [feeSettings, setFeeSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('platform');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchFeeSettings();
  }, []);

  const fetchFeeSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getAllFeeSettings();
      
      if (response.success) {
        setFeeSettings(response.data);
      } else {
        setError(response.message || 'Failed to load fee settings');
      }
    } catch (err) {
      setError('Failed to load fee settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (item, type) => {
    setEditingItem({ ...item, _type: type });
    setEditForm({ ...item });
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      let response;
      const { _type, fee_id, processor_id, tier_id, split_id } = editingItem;
      const id = fee_id || processor_id || tier_id || split_id;

      switch (_type) {
        case 'platform':
          response = await updatePlatformFee(id, editForm);
          break;
        case 'processor':
          response = await updateProcessorFee(id, editForm);
          break;
        case 'tiered':
          response = await updateTieredFee(id, editForm);
          break;
        case 'split':
          response = await updateFeeSplit(id, editForm);
          break;
        default:
          throw new Error('Invalid fee type');
      }

      if (response.success) {
        setSuccessMessage('Fee updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        cancelEditing();
        fetchFeeSettings();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to update fee: ${err.message}`);
    }
  };

  const handleToggleActive = async (item, type) => {
    try {
      const id = item.fee_id || item.processor_id || item.tier_id || item.split_id;
      const tableMap = {
        'platform': 'platform_fee_settings',
        'processor': 'processor_fee_settings',
        'tiered': 'tiered_fee_settings',
        'split': 'fee_split_settings'
      };
      
      const response = await toggleFeeActive(tableMap[type], id, !item.is_active);
      if (response.success) {
        setSuccessMessage(`Fee ${!item.is_active ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchFeeSettings();
      } else {
        throw new Error(response.message || 'Toggle failed');
      }
    } catch (err) {
      setError(`Failed to toggle fee status: ${err.message}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatFeeType = (type) => {
    const types = {
      'deposit': 'Deposit',
      'purchase': 'Purchase',
      'withdrawal': 'Withdrawal',
      'boost': 'Boost',
      'platform_withdrawal': 'Platform Withdrawal',
      'processor_withdrawal': 'Processor Withdrawal',
      'transaction': 'Transaction',
      'withdrawal_fee': 'Withdrawal Fee',
      'transfer': 'Transfer',
      'fee_split': 'Fee Split'
    };
    return types[type] || type;
  };

  // Field configurations for edit forms
  const getFormFields = (type) => {
    const baseFields = {
      platform: [
        { name: 'fee_percentage', label: 'Fee Percentage', type: 'percentage' },
        { name: 'fixed_fee', label: 'Fixed Fee', type: 'currency' },
        { name: 'min_fee', label: 'Minimum Fee', type: 'currency' },
        { name: 'max_fee', label: 'Maximum Fee', type: 'currency' },
        { name: 'description', label: 'Description', type: 'textarea' }
      ],
      processor: [
        { name: 'percentage', label: 'Percentage Fee', type: 'percentage' },
        { name: 'flat_fee', label: 'Flat Fee', type: 'currency' },
        { name: 'apply_flat_from_amount', label: 'Apply Flat From Amount', type: 'currency' },
        { name: 'max_fee', label: 'Maximum Fee', type: 'currency' },
        { name: 'description', label: 'Description', type: 'textarea' }
      ],
      tiered: [
        { name: 'min_amount', label: 'Minimum Amount', type: 'currency' },
        { name: 'max_amount', label: 'Maximum Amount', type: 'currency', placeholder: 'Leave empty for no limit' },
        { name: 'fee_amount', label: 'Fee Amount', type: 'currency' },
        { name: 'description', label: 'Description', type: 'textarea' }
      ],
      split: [
        { 
          name: 'transaction_type', 
          label: 'Transaction Type', 
          type: 'select',
          options: [
            { value: 'purchase', label: 'Purchase' },
            { value: 'deposit', label: 'Deposit' },
            { value: 'withdrawal', label: 'Withdrawal' }
          ]
        },
        { name: 'buyer_percentage', label: 'Buyer Percentage', type: 'percentage' },
        { name: 'seller_percentage', label: 'Seller Percentage', type: 'percentage' },
        { name: 'description', label: 'Description', type: 'textarea' }
      ]
    };
    
    return baseFields[type] || [];
  };

  if (loading && !feeSettings) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="h-40 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Fee Management</h1>
            <p className="text-gray-600 mt-1">Configure platform fees, processor fees, and fee splits</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchFeeSettings}
              className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheck className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {[
              { id: 'platform', icon: FiShield, label: 'Platform Fees' },
              { id: 'processor', icon: FiCreditCard, label: 'Processor Fees' },
              { id: 'tiered', icon: TbReceiptTax, label: 'Tiered Fees' },
              { id: 'split', icon: TbArrowsSplit, label: 'Fee Splits' }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#ff660e] text-[#ff660e]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  <Icon className="inline mr-2" />
                  {tab.label}
                </button>
              );
            })}
            <div className="flex-1 border-b-2 border-gray-200"></div>
          </nav>
        </div>

        <div className="p-6">
          {/* Toggle for showing inactive */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-[#0f0e0e]">
              {activeTab === 'platform' && 'Platform Fee Settings'}
              {activeTab === 'processor' && 'Processor Fee Settings'}
              {activeTab === 'tiered' && 'Tiered Fee Settings'}
              {activeTab === 'split' && 'Fee Split Settings'}
            </h2>
            <div className="flex items-center space-x-2">
              <FiFilter className="text-gray-400" />
              <label className="flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="mr-2 rounded border-gray-300 text-[#ff660e] focus:ring-[#ff660e]"
                />
                Show Inactive
              </label>
            </div>
          </div>

          {/* Platform Fees */}
          {activeTab === 'platform' && feeSettings?.platform_fees && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feeSettings.platform_fees
                .filter(fee => showInactive || fee.is_active)
                .map((fee) => {
                  const isEditing = editingItem?._type === 'platform' && editingItem.fee_id === fee.fee_id;
                  
                  return (
                    <FeeCard
                      key={fee.fee_id}
                      item={fee}
                      type="platform"
                      isEditing={isEditing}
                      onEdit={() => startEditing(fee, 'platform')}
                      onToggle={() => handleToggleActive(fee, 'platform')}
                    >
                      {isEditing ? (
                        <FeeEditForm
                          formData={editForm}
                          onInputChange={handleInputChange}
                          onSave={handleSave}
                          onCancel={cancelEditing}
                          type="platform"
                          fields={getFormFields('platform')}
                        />
                      ) : (
                        <div>
                          <div className="mb-4">
                            <p className="text-gray-600 text-sm">{fee.description}</p>
                            <span className="inline-block px-2 py-1 mt-2 bg-blue-50 text-blue-700 text-xs rounded">
                              {formatFeeType(fee.fee_type)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Percentage:</span>
                              <p className="font-medium">{fee.fee_percentage}%</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fixed Fee:</span>
                              <p className="font-medium">{formatCurrency(fee.fixed_fee)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Min Fee:</span>
                              <p className="font-medium">{formatCurrency(fee.min_fee)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Max Fee:</span>
                              <p className="font-medium">{fee.max_fee ? formatCurrency(fee.max_fee) : 'No limit'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </FeeCard>
                  );
                })}
            </div>
          )}

          {/* Processor Fees */}
          {activeTab === 'processor' && feeSettings?.processor_fees && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feeSettings.processor_fees
                .filter(fee => showInactive || fee.is_active)
                .map((fee) => {
                  const isEditing = editingItem?._type === 'processor' && editingItem.processor_id === fee.processor_id;
                  
                  return (
                    <FeeCard
                      key={fee.processor_id}
                      item={fee}
                      type="processor"
                      isEditing={isEditing}
                      onEdit={() => startEditing(fee, 'processor')}
                      onToggle={() => handleToggleActive(fee, 'processor')}
                    >
                      {isEditing ? (
                        <FeeEditForm
                          formData={editForm}
                          onInputChange={handleInputChange}
                          onSave={handleSave}
                          onCancel={cancelEditing}
                          type="processor"
                          fields={getFormFields('processor')}
                        />
                      ) : (
                        <div>
                          <div className="mb-4">
                            <p className="text-gray-600 text-sm">{fee.description}</p>
                            <div className="flex space-x-2 mt-2">
                              <span className="inline-block px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded">
                                {fee.processor_name?.toUpperCase()}
                              </span>
                              <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                                {formatFeeType(fee.fee_type)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Percentage:</span>
                              <p className="font-medium">{fee.percentage}%</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Flat Fee:</span>
                              <p className="font-medium">{formatCurrency(fee.flat_fee)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Flat From:</span>
                              <p className="font-medium">{formatCurrency(fee.apply_flat_from_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Max Fee:</span>
                              <p className="font-medium">{formatCurrency(fee.max_fee)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </FeeCard>
                  );
                })}
            </div>
          )}

          {/* Tiered Fees */}
          {activeTab === 'tiered' && feeSettings?.tiered_fees && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feeSettings.tiered_fees
                .filter(fee => showInactive || fee.is_active)
                .map((fee) => {
                  const isEditing = editingItem?._type === 'tiered' && editingItem.tier_id === fee.tier_id;
                  
                  return (
                    <FeeCard
                      key={fee.tier_id}
                      item={fee}
                      type="tiered"
                      isEditing={isEditing}
                      onEdit={() => startEditing(fee, 'tiered')}
                      onToggle={() => handleToggleActive(fee, 'tiered')}
                    >
                      {isEditing ? (
                        <FeeEditForm
                          formData={editForm}
                          onInputChange={handleInputChange}
                          onSave={handleSave}
                          onCancel={cancelEditing}
                          type="tiered"
                          fields={getFormFields('tiered')}
                        />
                      ) : (
                        <div>
                          <div className="mb-4">
                            <p className="text-gray-600 text-sm">{fee.description}</p>
                            <span className="inline-block px-2 py-1 mt-2 bg-blue-50 text-blue-700 text-xs rounded">
                              {formatFeeType(fee.fee_for)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Min Amount:</span>
                              <p className="font-medium">{formatCurrency(fee.min_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Max Amount:</span>
                              <p className="font-medium">
                                {fee.max_amount ? formatCurrency(fee.max_amount) : 'No limit'}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fee Amount:</span>
                              <p className="font-medium">{formatCurrency(fee.fee_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Fee Type:</span>
                              <p className="font-medium capitalize">{fee.fee_for}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </FeeCard>
                  );
                })}
            </div>
          )}

          {/* Fee Splits */}
          {activeTab === 'split' && feeSettings?.fee_splits && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feeSettings.fee_splits
                .filter(fee => showInactive || fee.is_active)
                .map((fee) => {
                  const isEditing = editingItem?._type === 'split' && editingItem.split_id === fee.split_id;
                  
                  return (
                    <FeeCard
                      key={fee.split_id}
                      item={fee}
                      type="split"
                      isEditing={isEditing}
                      onEdit={() => startEditing(fee, 'split')}
                      onToggle={() => handleToggleActive(fee, 'split')}
                    >
                      {isEditing ? (
                        <FeeEditForm
                          formData={editForm}
                          onInputChange={handleInputChange}
                          onSave={handleSave}
                          onCancel={cancelEditing}
                          type="split"
                          fields={getFormFields('split')}
                        />
                      ) : (
                        <div>
                          <div className="mb-4">
                            <p className="text-gray-600 text-sm">{fee.description}</p>
                            <span className="inline-block px-2 py-1 mt-2 bg-blue-50 text-blue-700 text-xs rounded">
                              {formatFeeType(fee.transaction_type)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Buyer %:</span>
                              <p className="font-medium">{fee.buyer_percentage}%</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Seller %:</span>
                              <p className="font-medium">{fee.seller_percentage}%</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total:</span>
                              <p className="font-medium">
                                {(parseFloat(fee.buyer_percentage) + parseFloat(fee.seller_percentage)).toFixed(1)}%
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Transaction Type:</span>
                              <p className="font-medium capitalize">{fee.transaction_type}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </FeeCard>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default FeeSettings;